import { z } from 'zod';

/**
 * 3D Model Generation Parameters
 * Schemas for all 3D generation model families and variants
 *
 * Model Families:
 * - Meshy: v6-preview (text-to-3d, image-to-3d), v5 (multi-image-to-3d)
 * - Hunyuan3D: v2 (base, mini, turbo, mini-turbo, multi-view, multi-view-turbo)
 * - Hunyuan World: image-to-world (panorama/3D world generation)
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

/**
 * Generation modes available across all model families
 */
export const GenerationModeSchema = z.enum([
  'text-to-3d',        // Text prompt → 3D model (Meshy only)
  'image-to-3d',       // Single image → 3D model (Meshy + Hunyuan)
  'multi-image-to-3d', // Multiple images → 3D model (Meshy v5 + Hunyuan multi-view)
  'image-to-world',    // Image → Panorama/3D world (Hunyuan World only)
  '3d-to-3d',          // 3D model → Segmented parts (Hunyuan Part only)
]);

/**
 * AI model families
 */
export const ModelFamilySchema = z.enum([
  'meshy',
  'hunyuan',
]);

/**
 * Meshy model variants
 */
export const MeshyModelSchema = z.enum([
  'v6-preview-text',      // fal-ai/meshy/v6-preview/text-to-3d
  'v6-preview-image',     // fal-ai/meshy/v6-preview/image-to-3d
  'v5-multi-image',       // fal-ai/meshy/v5/multi-image-to-3d
]);

/**
 * Hunyuan3D model variants
 */
export const HunyuanModelSchema = z.enum([
  'v2-base',              // fal-ai/hunyuan3d/v2 (standard)
  'v2-mini',              // fal-ai/hunyuan3d/v2/mini (lightweight, 5GB VRAM)
  'v2-turbo',             // fal-ai/hunyuan3d/v2/turbo (faster base)
  'v2-mini-turbo',        // fal-ai/hunyuan3d/v2/mini/turbo (fastest)
  'v2-multiview',         // fal-ai/hunyuan3d/v2/multi-view (better geometry)
  'v2-multiview-turbo',   // fal-ai/hunyuan3d/v2/multi-view/turbo (faster multi-view)
  'world',                // fal-ai/hunyuan_world/image-to-world
  'part',                 // fal-ai/hunyuan-part (3D model segmentation)
]);

/**
 * Mesh topology types (Meshy only)
 */
export const TopologySchema = z.enum(['quad', 'triangle']);

/**
 * Symmetry modes (Meshy only)
 */
export const SymmetryModeSchema = z.enum(['off', 'auto', 'on']);

/**
 * Art styles (Meshy text-to-3d only)
 */
export const ArtStyleSchema = z.enum(['realistic', 'sculpture']);

/**
 * Export format options
 */
export const ExportFormatSchema = z.enum(['glb', 'fbx', 'usdz', 'obj']);

// ============================================================================
// SHARED PARAMETER SCHEMAS
// ============================================================================

/**
 * Seed for reproducible generation (both families)
 */
export const SeedSchema = z.number().int().optional();

/**
 * Image URL or base64 data URI
 */
export const ImageInputSchema = z.string().min(1).refine(
  (val) => val.startsWith('https://') || val.startsWith('data:'),
  { message: 'Must be an HTTPS URL or data URI' }
);

/**
 * Text prompt with character limit
 */
export const PromptSchema = z.string().max(600);

/**
 * PBR texture generation settings
 */
export const PBRSettingsSchema = z.object({
  enabled: z.boolean().default(false),
});

// ============================================================================
// MESHY SCHEMAS
// ============================================================================

/**
 * Meshy Text-to-3D Parameters
 * Endpoint: fal-ai/meshy/v6-preview/text-to-3d
 * Pricing: $0.80/generation
 */
export const MeshyTextTo3DParamsSchema = z.object({
  // Required
  prompt: PromptSchema,

  // Mode
  mode: z.enum(['preview', 'full']).default('full'),

  // Optional styling
  art_style: ArtStyleSchema.default('realistic'),

  // Mesh quality
  topology: TopologySchema.default('triangle'),
  target_polycount: z.number().int().min(100).max(300000).default(30000),
  should_remesh: z.boolean().default(true),
  symmetry_mode: SymmetryModeSchema.default('auto'),

  // Pose control
  is_a_t_pose: z.boolean().default(false),

  // Texturing (full mode only)
  enable_pbr: z.boolean().default(false),
  enable_prompt_expansion: z.boolean().default(false),
  texture_prompt: PromptSchema.optional(),
  texture_image_url: ImageInputSchema.optional(),

  // Reproducibility
  seed: SeedSchema,

  // Safety
  enable_safety_checker: z.boolean().default(true),
}).refine(
  (data) => {
    // texture_prompt and texture_image_url are mutually exclusive
    if (data.texture_prompt && data.texture_image_url) {
      return false;
    }
    return true;
  },
  {
    message: 'Cannot specify both texture_prompt and texture_image_url',
  }
);

/**
 * Meshy Image-to-3D Parameters
 * Endpoint: fal-ai/meshy/v6-preview/image-to-3d
 */
export const MeshyImageTo3DParamsSchema = z.object({
  // Required
  image_url: ImageInputSchema,

  // Mesh quality
  topology: TopologySchema.default('triangle'),
  target_polycount: z.number().int().min(100).max(300000).default(30000),
  symmetry_mode: SymmetryModeSchema.default('auto'),
  should_remesh: z.boolean().default(true),
  should_texture: z.boolean().default(true),

  // Texturing
  enable_pbr: z.boolean().default(false),
  texture_prompt: PromptSchema.optional(),
  texture_image_url: ImageInputSchema.optional(),

  // Pose control
  is_a_t_pose: z.boolean().default(false),

  // Safety
  enable_safety_checker: z.boolean().default(true),
}).refine(
  (data) => {
    // texture_prompt and texture_image_url are mutually exclusive
    if (data.texture_prompt && data.texture_image_url) {
      return false;
    }
    return true;
  },
  {
    message: 'Cannot specify both texture_prompt and texture_image_url',
  }
);

/**
 * Meshy Multi-Image-to-3D Parameters
 * Endpoint: fal-ai/meshy/v5/multi-image-to-3d
 * Note: Only available with Meshy-5 model
 * Cost: 20 credits (Meshy-6), 5 credits (Meshy-5)
 */
export const MeshyMultiImageTo3DParamsSchema = z.object({
  // Required: 1-4 images from different angles
  image_urls: z.array(ImageInputSchema).min(1).max(4),

  // Model control
  symmetry_mode: SymmetryModeSchema.default('auto'),
  should_remesh: z.boolean().default(true),
  should_texture: z.boolean().default(true),

  // Texturing
  enable_pbr: z.boolean().default(false),
  texture_prompt: PromptSchema.optional(),
  texture_image_url: ImageInputSchema.optional(),

  // Pose control
  at_pose: z.boolean().default(false), // Note: Different param name than single-image

  // Safety
  moderation: z.boolean().default(true),
}).refine(
  (data) => {
    // texture_prompt and texture_image_url are mutually exclusive
    if (data.texture_prompt && data.texture_image_url) {
      return false;
    }
    return true;
  },
  {
    message: 'Cannot specify both texture_prompt and texture_image_url',
  }
);

// ============================================================================
// HUNYUAN3D SCHEMAS
// ============================================================================

/**
 * Hunyuan3D Single-Image Parameters
 * Endpoints: v2-base, v2-mini, v2-turbo, v2-mini-turbo
 * Speed: Standard (5-10 min), Turbo (10-25s), Mini-Turbo (5-15s)
 */
export const HunyuanSingleImageParamsSchema = z.object({
  // Required
  input_image_url: ImageInputSchema,

  // Generation control
  num_inference_steps: z.number().int().min(1).max(50).default(50),
  guidance_scale: z.number().min(0).max(20).default(7.5),
  octree_resolution: z.number().int().min(1).max(1024).default(256),

  // Texturing (3x cost if enabled)
  textured_mesh: z.boolean().default(false),

  // Reproducibility
  seed: SeedSchema,
});

/**
 * Hunyuan3D Multi-View Parameters
 * Endpoints: v2-multiview, v2-multiview-turbo
 * Requires 3 images: front, back, left
 */
export const HunyuanMultiViewParamsSchema = z.object({
  // Required: 3 images from specific angles
  front_image_url: ImageInputSchema,
  back_image_url: ImageInputSchema,
  left_image_url: ImageInputSchema,

  // Generation control
  num_inference_steps: z.number().int().min(1).max(50).default(50),
  guidance_scale: z.number().min(0).max(20).default(7.5),
  octree_resolution: z.number().int().min(1).max(1024).default(256),

  // Texturing (3x cost if enabled)
  textured_mesh: z.boolean().default(false),

  // Reproducibility
  seed: SeedSchema,
});

/**
 * Hunyuan World Image-to-World Parameters
 * Endpoint: fal-ai/hunyuan_world/image-to-world
 * Creates panorama/3D world from single image
 * Pricing: $0.30/request
 */
export const HunyuanWorldParamsSchema = z.object({
  // Required
  image_url: ImageInputSchema,
  labels_fg1: z.string().min(1), // First foreground object labels (e.g., "tree, grass, sky")
  labels_fg2: z.string().min(1), // Second foreground object labels (e.g., "mountain, water")
  classes: z.string().min(1),    // Scene classes (e.g., "nature, landscape")

  // Optional
  export_drc: z.boolean().default(false), // Dynamic Resource Configuration export
});

// ============================================================================
// UNION TYPES
// ============================================================================

/**
 * All 3D generation parameters (discriminated union)
 * Discriminated on '_type' field for efficient parsing
 */
export const ThreeDGenerationParamsSchema = z.discriminatedUnion('_type', [
  z.object({
    _type: z.literal('meshy-text-to-3d'),
    // Required
    prompt: PromptSchema,
    // Mode
    mode: z.enum(['preview', 'full']).default('full'),
    // Optional styling
    art_style: ArtStyleSchema.default('realistic'),
    // Mesh quality
    topology: TopologySchema.default('triangle'),
    target_polycount: z.number().int().min(100).max(300000).default(30000),
    should_remesh: z.boolean().default(true),
    symmetry_mode: SymmetryModeSchema.default('auto'),
    // Pose control
    is_a_t_pose: z.boolean().default(false),
    // Texturing
    enable_pbr: z.boolean().default(false),
    enable_prompt_expansion: z.boolean().default(false),
    texture_prompt: PromptSchema.optional(),
    texture_image_url: ImageInputSchema.optional(),
    // Reproducibility
    seed: SeedSchema,
    // Safety
    enable_safety_checker: z.boolean().default(true),
  }),
  z.object({
    _type: z.literal('meshy-image-to-3d'),
    // Required
    image_url: ImageInputSchema,
    // Mesh quality
    topology: TopologySchema.default('triangle'),
    target_polycount: z.number().int().min(100).max(300000).default(30000),
    symmetry_mode: SymmetryModeSchema.default('auto'),
    should_remesh: z.boolean().default(true),
    should_texture: z.boolean().default(true),
    // Texturing
    enable_pbr: z.boolean().default(false),
    texture_prompt: PromptSchema.optional(),
    texture_image_url: ImageInputSchema.optional(),
    // Pose control
    is_a_t_pose: z.boolean().default(false),
    // Safety
    enable_safety_checker: z.boolean().default(true),
  }),
  z.object({
    _type: z.literal('meshy-multi-image-to-3d'),
    // Required
    image_urls: z.array(ImageInputSchema).min(1).max(4),
    // Model control
    symmetry_mode: SymmetryModeSchema.default('auto'),
    should_remesh: z.boolean().default(true),
    should_texture: z.boolean().default(true),
    // Texturing
    enable_pbr: z.boolean().default(false),
    texture_prompt: PromptSchema.optional(),
    texture_image_url: ImageInputSchema.optional(),
    // Pose control
    at_pose: z.boolean().default(false),
    // Safety
    moderation: z.boolean().default(true),
  }),
  z.object({
    _type: z.literal('hunyuan-single-image'),
    // Required
    input_image_url: ImageInputSchema,
    // Generation control
    num_inference_steps: z.number().int().min(1).max(50).default(50),
    guidance_scale: z.number().min(0).max(20).default(7.5),
    octree_resolution: z.number().int().min(1).max(1024).default(256),
    // Texturing
    textured_mesh: z.boolean().default(false),
    // Reproducibility
    seed: SeedSchema,
  }),
  z.object({
    _type: z.literal('hunyuan-multi-view'),
    // Required
    front_image_url: ImageInputSchema,
    back_image_url: ImageInputSchema,
    left_image_url: ImageInputSchema,
    // Generation control
    num_inference_steps: z.number().int().min(1).max(50).default(50),
    guidance_scale: z.number().min(0).max(20).default(7.5),
    octree_resolution: z.number().int().min(1).max(1024).default(256),
    // Texturing
    textured_mesh: z.boolean().default(false),
    // Reproducibility
    seed: SeedSchema,
  }),
  z.object({
    _type: z.literal('hunyuan-world'),
    // Required
    image_url: ImageInputSchema,
    labels_fg1: z.string().min(1),
    labels_fg2: z.string().min(1),
    classes: z.string().min(1),
    // Optional
    export_drc: z.boolean().default(false),
  }),
  z.object({
    _type: z.literal('hunyuan-part'),
    // Required
    model_file_url: z.string().min(1).refine(
      (val) => val.startsWith('https://') || val.startsWith('data:'),
      { message: 'Must be an HTTPS URL or data URI' }
    ), // URL to .glb or .obj file
    // Point prompt for segmentation (3D coordinates in normalized space)
    point_prompt_x: z.number().min(-1).max(1).default(0),
    point_prompt_y: z.number().min(-1).max(1).default(0),
    point_prompt_z: z.number().min(-1).max(1).default(0),
    // Sampling parameters
    point_num: z.number().int().min(10000).max(500000).default(100000),
    use_normal: z.boolean().default(true),
    noise_std: z.number().min(0).max(0.02).default(0),
    // Reproducibility
    seed: SeedSchema,
  }),
]);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type GenerationMode = z.infer<typeof GenerationModeSchema>;
export type ModelFamily = z.infer<typeof ModelFamilySchema>;
export type MeshyModel = z.infer<typeof MeshyModelSchema>;
export type HunyuanModel = z.infer<typeof HunyuanModelSchema>;
export type Topology = z.infer<typeof TopologySchema>;
export type SymmetryMode = z.infer<typeof SymmetryModeSchema>;
export type ArtStyle = z.infer<typeof ArtStyleSchema>;
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

export type MeshyTextTo3DParams = z.infer<typeof MeshyTextTo3DParamsSchema>;
export type MeshyImageTo3DParams = z.infer<typeof MeshyImageTo3DParamsSchema>;
export type MeshyMultiImageTo3DParams = z.infer<typeof MeshyMultiImageTo3DParamsSchema>;
export type HunyuanSingleImageParams = z.infer<typeof HunyuanSingleImageParamsSchema>;
export type HunyuanMultiViewParams = z.infer<typeof HunyuanMultiViewParamsSchema>;
export type HunyuanWorldParams = z.infer<typeof HunyuanWorldParamsSchema>;
export type HunyuanPartParams = z.infer<typeof ThreeDGenerationParamsSchema> & { _type: 'hunyuan-part' };
export type ThreeDGenerationParams = z.infer<typeof ThreeDGenerationParamsSchema>;
